import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { 
  getSetupStatus, 
  createParticipants,
  updateParticipants, 
  addStartupsBulk, 
  deleteStartup, 
  getStartups,
  resetDatabase,
  getAllQRCodes,
  getAllParticipants,
  checkAuth
} from '../services/api';
import AdminLogin from '../components/AdminLogin';

// Lazy load jspdf only when needed (for PDF export)
let jsPDFClass = null;
const loadJsPDF = async () => {
  if (!jsPDFClass) {
    try {
      // Try different import methods for different jspdf versions
      const jsPDFModule = await import('jspdf');
      
      // jspdf 4.0+ uses named export { jsPDF }
      if (jsPDFModule.jsPDF) {
        jsPDFClass = jsPDFModule.jsPDF;
      } 
      // Older versions might use default export
      else if (jsPDFModule.default) {
        // Check if default is the class or an object with jsPDF
        if (typeof jsPDFModule.default === 'function') {
          jsPDFClass = jsPDFModule.default;
        } else if (jsPDFModule.default.jsPDF) {
          jsPDFClass = jsPDFModule.default.jsPDF;
        }
      }
      
      if (!jsPDFClass) {
        console.error('jsPDF module structure:', Object.keys(jsPDFModule));
        throw new Error('jsPDF konnte nicht geladen werden. Modul-Struktur nicht erkannt.');
      }
    } catch (error) {
      console.error('Error loading jspdf:', error);
      throw new Error('Fehler beim Laden von jspdf: ' + error.message);
    }
  }
  return jsPDFClass;
};

function Setup() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(null); // null = checking, true = authenticated, false = not authenticated
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('config');
  
  // Participant configuration
  const [juryCount, setJuryCount] = useState(14);
  const [audienceCount, setAudienceCount] = useState(70);
  const [juryBudget, setJuryBudget] = useState(200000);
  const [audienceBudget, setAudienceBudget] = useState(20000);
  
  // Startup configuration
  const [startupList, setStartupList] = useState([]);
  const [startupInput, setStartupInput] = useState('');
  const [existingStartups, setExistingStartups] = useState([]);
  
  // QR Codes
  const [qrCodes, setQrCodes] = useState([]);
  const [loadingQRCodes, setLoadingQRCodes] = useState(false);
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check authentication status
  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const response = await checkAuth();
      setAuthenticated(response.data.authenticated);
      if (response.data.authenticated) {
        loadStatus();
      } else {
        setLoading(false);
      }
    } catch (error) {
      setAuthenticated(false);
      setLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setAuthenticated(true);
    loadStatus();
  };

  const loadStatus = async () => {
    try {
      setLoading(true);
      const statusRes = await getSetupStatus();
      setStatus(statusRes.data);
      
      let shouldLoadQRCodes = false;
      
      if (statusRes.data.configured) {
        // Load existing startups and participants in parallel
        const [startupsRes, participantsRes] = await Promise.all([
          getStartups(),
          statusRes.data.participantCount > 0 ? getAllParticipants() : Promise.resolve({ data: [] })
        ]);
        
        setExistingStartups(startupsRes.data);
        
        // Load participant counts and set them in state
        if (statusRes.data.participantCount > 0 && participantsRes.data.length > 0) {
          const participants = participantsRes.data;
          const juryCount = participants.filter(p => p.type === 'jury').length;
          const audienceCount = participants.filter(p => p.type === 'audience').length;
          if (juryCount > 0) setJuryCount(juryCount);
          if (audienceCount > 0) setAudienceCount(audienceCount);
          // Set budgets from first participant of each type
          const firstJury = participants.find(p => p.type === 'jury');
          const firstAudience = participants.find(p => p.type === 'audience');
          if (firstJury) setJuryBudget(firstJury.initial_budget);
          if (firstAudience) setAudienceBudget(firstAudience.initial_budget);
          
          shouldLoadQRCodes = true;
        }
      }
      
      // Load QR codes asynchronously after main loading is done (non-blocking)
      if (shouldLoadQRCodes) {
        loadQRCodes();
      }
    } catch (err) {
      console.error('Error loading status:', err);
      setError('Error loading status');
      if (err.response?.status === 401) {
        // Unauthorized - session expired
        setAuthenticated(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadQRCodes = async () => {
    try {
      setLoadingQRCodes(true);
      const res = await getAllQRCodes();
      setQrCodes(res.data.qrCodes || []);
    } catch (err) {
      console.error('Error loading QR codes:', err);
      setError('Error loading QR codes');
    } finally {
      setLoadingQRCodes(false);
    }
  };

  const exportQRToPDF = async (type = 'all') => {
    try {
      // Load jsPDF class dynamically
      const jsPDF = await loadJsPDF();
      
      const codesToExport = type === 'jury' 
        ? juryQRCodes 
        : type === 'audience' 
        ? audienceQRCodes 
        : qrCodes;
      
      if (codesToExport.length === 0) {
        setError('Keine QR-Codes zum Exportieren vorhanden');
        return;
      }

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;
      const qrSize = 50; // Size in mm
      const itemsPerRow = 3;
      const spacing = (pageWidth - 2 * margin - itemsPerRow * qrSize) / (itemsPerRow - 1);
      
      let x = margin;
      let y = margin + 10;
      let itemCount = 0;

      // Add title
      doc.setFontSize(16);
      doc.setFont(undefined, 'bold');
      const title = type === 'jury' ? 'Jury QR-Codes' : type === 'audience' ? 'Audience QR-Codes' : 'Alle QR-Codes';
      doc.text(title, pageWidth / 2, y, { align: 'center' });
      y += 10;

      for (const qr of codesToExport) {
        // Check if we need a new page
        if (itemCount > 0 && itemCount % itemsPerRow === 0) {
          x = margin;
          y += qrSize + 25;
          
          if (y + qrSize + 15 > pageHeight - margin) {
            doc.addPage();
            y = margin + 10;
          }
        }

        // Use the QR code Data URL directly from backend (already base64 encoded)
        if (!qr.qrCode) {
          console.warn('QR Code Data URL fehlt für:', qr.participant.name);
          continue;
        }

        // Add QR code image to PDF (qr.qrCode is already a data URL from backend)
        doc.addImage(qr.qrCode, 'PNG', x, y, qrSize, qrSize);
        
        // Add participant name and type below QR code
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        const nameText = qr.participant.name.length > 18 
          ? qr.participant.name.substring(0, 18) + '...' 
          : qr.participant.name;
        doc.text(nameText, x + qrSize / 2, y + qrSize + 5, { 
          align: 'center',
          maxWidth: qrSize
        });
        
        doc.setFontSize(7);
        doc.setTextColor(100, 100, 100);
        doc.text(qr.participant.type, x + qrSize / 2, y + qrSize + 9, { 
          align: 'center' 
        });
        doc.setTextColor(0, 0, 0);

        // Move to next position
        x += qrSize + spacing;
        itemCount++;
      }

      // Save PDF
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `QR-Codes_${type}_${dateStr}.pdf`;
      doc.save(filename);
      
      setSuccess(`PDF erfolgreich erstellt: ${filename}`);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError('Fehler beim Erstellen des PDFs: ' + err.message);
    }
  };

  const handleAddStartup = () => {
    if (startupInput.trim() === '') return;
    if (startupList.includes(startupInput.trim())) {
      setError('Startup already in list');
      return;
    }
    setStartupList([...startupList, startupInput.trim()]);
    setStartupInput('');
    setError('');
  };

  const handleRemoveStartup = (index) => {
    setStartupList(startupList.filter((_, i) => i !== index));
  };

  const handleCreateStartups = async () => {
    if (startupList.length === 0) {
      setError('Bitte fügen Sie mindestens ein Startup hinzu');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const startups = startupList.map(name => ({ name }));
      await addStartupsBulk(startups);
      setSuccess(`${startupList.length} startups successfully created`);
      setStartupList([]); // Clear the list after successful creation
      await loadStatus();
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating startups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateParticipants = async () => {
    if (juryCount < 1 || audienceCount < 1) {
      setError('Anzahl muss mindestens 1 sein');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Always use updateParticipants - it handles both creating (when count is 0) and updating
      await updateParticipants({
        juryCount: parseInt(juryCount),
        audienceCount: parseInt(audienceCount),
        juryBudget: parseFloat(juryBudget),
        audienceBudget: parseFloat(audienceBudget)
      });
      
      const hasExistingParticipants = status?.participantCount > 0;
      setSuccess(hasExistingParticipants 
        ? `${juryCount} jury members and ${audienceCount} audience members successfully updated`
        : `${juryCount} jury members and ${audienceCount} audience members successfully created`
      );
      
      await loadStatus();
      // Load QR codes after creating/updating participants
      await loadQRCodes();
      setActiveTab('qrcodes');
    } catch (err) {
      setError(err.response?.data?.error || 'Error creating/updating participants');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Do you really want to delete all data? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await resetDatabase();
      setStatus({ configured: false, participantCount: 0, startupCount: 0 });
      setExistingStartups([]);
      setQrCodes([]);
      setActiveTab('config');
      setSuccess('Datenbank erfolgreich zurückgesetzt');
    } catch (err) {
      setError(err.response?.data?.error || 'Error resetting');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStartup = async (id) => {
    try {
      await deleteStartup(id);
      await loadStatus();
      setSuccess('Startup erfolgreich gelöscht');
    } catch (err) {
      setError(err.response?.data?.error || 'Error deleting startup');
    }
  };

  // Show login if not authenticated
  if (authenticated === false) {
    return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // Show loading while checking authentication
  if (authenticated === null || (loading && !status)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const juryQRCodes = qrCodes.filter(qr => qr.participant.type === 'jury');
  const audienceQRCodes = qrCodes.filter(qr => qr.participant.type === 'audience');

  return (
    <div className="min-h-screen p-6 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        <div className="card mb-8 bg-gradient-to-r from-gray-800 to-gray-900 text-white">
          <h1 className="text-4xl font-bold mb-2">Event Setup</h1>
          <p className="text-gray-300">Configuration of setup</p>
        </div>

        {status?.configured && (
          <div className="card mb-6 bg-yellow-50 border border-yellow-200">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-yellow-800 font-semibold">Event already configured</p>
                <p className="text-yellow-600 text-sm mt-1">
                  {status.participantCount} Participants • {status.startupCount} Startups
                </p>
              </div>
              <button
                onClick={handleReset}
                className="btn-secondary text-sm"
              >
                Reset
              </button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('config')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'config'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Configuration
          </button>
          <button
            onClick={() => {
              setActiveTab('qrcodes');
              if (status?.participantCount > 0) {
                loadQRCodes();
              }
            }}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'qrcodes'
                ? 'border-b-2 border-primary-600 text-primary-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            QR-Codes {status?.participantCount > 0 && `(${qrCodes.length})`}
          </button>
        </div>

        {error && (
          <div className="card mb-6 bg-red-50 border border-red-200 text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="card mb-6 bg-green-50 border border-green-200 text-green-700">
            {success}
          </div>
        )}

        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <>
            {/* Step 1: Startups */}
            <div className="card mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Configure Startups</h2>
              
              {existingStartups.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-2">Existing startups:</p>
                  <div className="space-y-2">
                    {existingStartups.map(startup => (
                      <div key={startup.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                        <span className="font-medium">{startup.name}</span>
                        <button
                          onClick={() => handleDeleteStartup(startup.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={startupInput}
                    onChange={(e) => setStartupInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddStartup()}
                    placeholder="Enter startup name"
                    className="input-field flex-1"
                  />
                  <button
                    onClick={handleAddStartup}
                    className="btn-primary"
                  >
                    Add
                  </button>
                </div>

                {startupList.length > 0 && (
                  <div className="space-y-2">
                    {startupList.map((startup, index) => (
                      <div key={index} className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                        <span className="font-medium">{startup}</span>
                        <button
                          onClick={() => handleRemoveStartup(index)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {startupList.length > 0 && (
                  <button
                    onClick={handleCreateStartups}
                    className="btn-primary w-full"
                    disabled={loading}
                  >
                    Create Startups ({startupList.length})
                  </button>
                )}
              </div>
            </div>

            {/* Step 2: Participants */}
            <div className="card mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Configure Participants</h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Anzahl Jury-Mitglieder
                  </label>
                  <input
                    type="number"
                    value={juryCount}
                    onChange={(e) => setJuryCount(parseInt(e.target.value) || 0)}
                    min="1"
                    className="input-field w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget per Jury Member (€)
                  </label>
                  <input
                    type="number"
                    value={juryBudget}
                    onChange={(e) => setJuryBudget(parseFloat(e.target.value) || 0)}
                    min="0"
                    step="1000"
                    className="input-field w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of Audience Members
                  </label>
                  <input
                    type="number"
                    value={audienceCount}
                    onChange={(e) => setAudienceCount(parseInt(e.target.value) || 0)}
                    min="1"
                    className="input-field w-full"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget per Audience Member (€)
                  </label>
                  <input
                    type="number"
                    value={audienceBudget}
                    onChange={(e) => setAudienceBudget(parseFloat(e.target.value) || 0)}
                    min="0"
                    step="1000"
                    className="input-field w-full"
                  />
                </div>
              </div>

              <button
                onClick={handleCreateParticipants}
                className="btn-primary w-full"
                disabled={juryCount < 1 || audienceCount < 1 || loading}
              >
                {status?.participantCount > 0 
                  ? `Update Participants (${juryCount} Jury + ${audienceCount} Audience)`
                  : `Create/Update Participants (${juryCount} Jury + ${audienceCount} Audience)`
                }
              </button>
            </div>
          </>
        )}

        {/* QR Codes Tab */}
        {activeTab === 'qrcodes' && (
          <div className="space-y-8">
            {!status?.participantCount || status.participantCount === 0 ? (
              <div className="card text-center py-12">
                <div className="text-xl text-gray-600 mb-4">No participants created yet</div>
                <p className="text-gray-500">Please create participants first in the Configuration tab.</p>
              </div>
            ) : loadingQRCodes ? (
              <div className="card text-center py-12">
                <div className="text-xl text-gray-600">Loading QR codes...</div>
              </div>
            ) : (
              <>
                {/* Export Buttons */}
                <div className="card mb-6">
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => exportQRToPDF('all')}
                      className="btn-primary"
                      disabled={qrCodes.length === 0}
                    >
                      📄 Alle QR-Codes als PDF exportieren
                    </button>
                    {juryQRCodes.length > 0 && (
                      <button
                        onClick={() => exportQRToPDF('jury')}
                        className="btn-secondary"
                      >
                        📄 Jury QR-Codes exportieren
                      </button>
                    )}
                    {audienceQRCodes.length > 0 && (
                      <button
                        onClick={() => exportQRToPDF('audience')}
                        className="btn-secondary"
                      >
                        📄 Audience QR-Codes exportieren
                      </button>
                    )}
                  </div>
                </div>

                {/* Jury QR Codes */}
                {juryQRCodes.length > 0 && (
                  <div className="card">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Jury QR-Codes ({juryQRCodes.length})
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {juryQRCodes.map((qr) => (
                        <div key={qr.participant.id} className="text-center p-4 bg-gray-50 rounded-lg">
                          <div className="bg-white p-3 rounded-lg mb-3 flex justify-center">
                            <QRCodeSVG value={qr.url} size={150} />
                          </div>
                          <p className="font-semibold text-gray-900 text-sm mb-1">{qr.participant.name}</p>
                          <p className="text-xs text-gray-500">Jury</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audience QR Codes */}
                {audienceQRCodes.length > 0 && (
                  <div className="card">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Audience QR Codes ({audienceQRCodes.length})
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                      {audienceQRCodes.map((qr) => (
                        <div key={qr.participant.id} className="text-center p-4 bg-gray-50 rounded-lg">
                          <div className="bg-white p-3 rounded-lg mb-3 flex justify-center">
                            <QRCodeSVG value={qr.url} size={150} />
                          </div>
                          <p className="font-semibold text-gray-900 text-sm mb-1">{qr.participant.name}</p>
                          <p className="text-xs text-gray-500">Audience</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {juryQRCodes.length === 0 && audienceQRCodes.length === 0 && (
                  <div className="card text-center py-12">
                    <div className="text-xl text-gray-600">No QR codes available</div>
                    <p className="text-gray-500 mt-2">QR codes will be generated after participants are created.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="card bg-blue-50 border border-blue-200 mt-6">
          <p className="text-blue-800">
            <strong>Tip:</strong> After configuration, you can track all investments through the{' '}
            <a href="/admin" className="underline font-semibold">Admin Dashboard</a>.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Setup;
